import type { SourceErrorCode } from '../core/definitions';

export interface MappedError {
  code: SourceErrorCode;
  message: string;
}

interface ErrorLike {
  name?: unknown;
  message?: unknown;
}

/**
 * Maps a getUserMedia / getDisplayMedia / media-element failure onto the
 * contract's error codes. For screen capture a refused picker is a user
 * choice rather than a permission problem, so it maps to 'aborted'.
 */
export function mapMediaError(err: unknown, opts: { displayCapture?: boolean } = {}): MappedError {
  const e = (typeof err === 'object' && err !== null ? err : {}) as ErrorLike;
  const name = typeof e.name === 'string' ? e.name : '';
  const message = typeof e.message === 'string' && e.message ? e.message : name || 'Unknown error';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return { code: opts.displayCapture ? 'aborted' : 'permission-denied', message };
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'DevicesNotFoundError':
      return { code: 'not-found', message };
    case 'NotReadableError':
    case 'TrackStartError':
      return { code: 'in-use', message };
    case 'AbortError':
      return { code: 'aborted', message };
    case 'NotSupportedError':
      return { code: 'unsupported', message };
    default:
      return { code: 'unknown', message };
  }
}

/** Thrown by loaders so callers can report a precise status. */
export class SourceLoadError extends Error {
  constructor(
    readonly code: SourceErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'SourceLoadError';
  }
}

export function toMapped(err: unknown): MappedError {
  if (err instanceof SourceLoadError) return { code: err.code, message: err.message };
  return mapMediaError(err);
}

/**
 * Status for a rejected HTMLMediaElement.play(). An interrupted load (AbortError) is not a failure and
 * yields null; a blocked autoplay leaves the source idle with a message so the UI can offer Play.
 */
export function mapPlayFailure(err: unknown): { state: 'idle' | 'error'; error: MappedError } | null {
  const e = (typeof err === 'object' && err !== null ? err : {}) as ErrorLike;
  const name = typeof e.name === 'string' ? e.name : '';
  const message = typeof e.message === 'string' && e.message ? e.message : name || 'play() failed';
  if (name === 'AbortError') return null;
  if (name === 'NotAllowedError') {
    return { state: 'idle', error: { code: 'aborted', message: `Playback was blocked by the browser's autoplay policy: ${message}` } };
  }
  if (name === 'NotSupportedError') return { state: 'error', error: { code: 'load-failed', message } };
  return { state: 'error', error: { code: 'unknown', message } };
}
